import { useState, useId } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { DrawerTopBarWithAction } from "@/components/Drawer/DrawerTopBar";
import { PriceRangeSlider } from "@/components/Sliders/PriceRangeSlider";
import { BoardBasisFilter } from "@/features/hotels/components/hotels-list/BoardBasisFilter";
import { HotelClassFilter } from "@/features/hotels/components/hotels-list/HotelClassFilter";
import HotelsListsSort from "@/features/hotels/components/hotels-list/HotelsListSort";
import type {
  HotelsSortValue,
  IHotelsFilters,
} from "@/features/hotels/types/types";
import {
  BOARD_BASIS_OPTIONS,
  HOTEL_CLASSES,
} from "@/features/hotels/utils/constants";
import { initialSortValue } from "@/features/hotels/utils/initialHotelsValues";
import { useToggleSelectAll } from "@/hooks/useToggleSelectAll";

type Props = {
  defaultFilters: IHotelsFilters;
  filters: IHotelsFilters;
  bookingNights: number;
  sortValue: HotelsSortValue;
  onFiltersChange: (
    newFilters: Partial<IHotelsFilters>,
    newSort: HotelsSortValue,
  ) => void;
  onClose: () => void;
};

export function HotelsFilters({
  defaultFilters,
  filters,
  bookingNights,
  sortValue,
  onFiltersChange,
  onClose,
}: Props) {
  const priceHeaderId = useId();
  const intl = useIntl();
  const [localFilters, setLocalFilters] = useState<IHotelsFilters>({
    ...filters,
  });
  const [sort, setSort] = useState<HotelsSortValue>(sortValue);

  const {
    manuallySelectedAll: manuallySelectedAllHotelClasses,
    setManuallySelectedAll: setManuallySelectedAllHotelClasses,
    areAllSelected: areAllHotelClassesSelected,
    toggle: toggleHotelClasses,
  } = useToggleSelectAll<number>({
    selected: localFilters.starRating,
    all: HOTEL_CLASSES,
  });

  const areHotelClassesInDefaultState =
    areAllHotelClassesSelected && !manuallySelectedAllHotelClasses;

  const {
    manuallySelectedAll: manuallySelectedAllBoardBasis,
    setManuallySelectedAll: setManuallySelectedAllBoardBasis,
    areAllSelected: areAllBoardBasisSelected,
    toggle: toggleBoardBasis,
  } = useToggleSelectAll<string>({
    selected: localFilters.boardBasis || [],
    all: BOARD_BASIS_OPTIONS,
  });

  const areBoardBasisInDefaultState =
    areAllBoardBasisSelected && !manuallySelectedAllBoardBasis;

  return (
    <>
      <DrawerTopBarWithAction
        onCloseClick={onClose}
        title={intl.formatMessage({
          id: "hotels.filters.filters",
          defaultMessage: "Filters",
        })}
        actionElement={
          <Button
            onClick={(e) => {
              setLocalFilters(defaultFilters);
              setSort(initialSortValue);
              e.currentTarget.blur();
            }}
            variant="text"
            size="small"
          >
            {intl.formatMessage({
              id: "clearFilters",
              defaultMessage: "Clear filters",
            })}
          </Button>
        }
      />

      <div className="-mx-main my-4 flex flex-grow flex-col gap-8 overflow-y-auto overscroll-x-none px-main">
        <div>
          <h3 className="mb-4 text-xl font-bold text-black">
            {intl.formatMessage({
              id: "hotels.filters.sort",
              defaultMessage: "Sort",
            })}
          </h3>
          <HotelsListsSort value={sort} onChange={setSort} />
        </div>

        <div>
          <h3 id={priceHeaderId} className="mb-4 text-xl font-bold text-black">
            {intl.formatMessage({
              id: "hotels.filters.priceRangePerNight",
              defaultMessage: "Price Range (per night)",
            })}
          </h3>
          <PriceRangeSlider
            min={defaultFilters.priceRange[0]}
            max={defaultFilters.priceRange[1]}
            aria-labelledby={priceHeaderId}
            value={[localFilters.priceRange[0], localFilters.priceRange[1]]}
            scale={(value) => value / bookingNights}
            descale={(value) => Math.round(value * bookingNights)}
            onChange={(newValue) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                priceRange: newValue,
              }));
            }}
          />
        </div>

        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="shrink-0 text-xl font-bold text-black">
              {intl.formatMessage({
                id: "hotels.filters.boardBasis",
                defaultMessage: "Board Basis",
              })}
            </h3>
            <Button
              onClick={(e) => {
                if (areBoardBasisInDefaultState) {
                  setLocalFilters((prevFilters) => ({
                    ...prevFilters,
                    boardBasis: BOARD_BASIS_OPTIONS,
                  }));
                  setManuallySelectedAllBoardBasis(true);
                } else {
                  setLocalFilters((prevFilters) => ({
                    ...prevFilters,
                    boardBasis: defaultFilters.boardBasis,
                  }));
                  setManuallySelectedAllBoardBasis(false);
                }

                // Remove focus from the button after click
                e.currentTarget.blur();
              }}
              variant="text"
              size="small"
            >
              {areBoardBasisInDefaultState
                ? intl.formatMessage({
                    id: "selectAll",
                    defaultMessage: "Select All",
                  })
                : intl.formatMessage({
                    id: "clearFilter",
                    defaultMessage: "Clear filter",
                  })}
            </Button>
          </div>

          <BoardBasisFilter
            selected={localFilters.boardBasis}
            userManuallySelectedAll={manuallySelectedAllBoardBasis}
            onChange={(_e, checked, option) => {
              setLocalFilters((prevFilter) => {
                const newClasses = toggleBoardBasis(
                  prevFilter.boardBasis || [],
                  option,
                  checked,
                ).sort((a, b) => a.localeCompare(b));
                return { ...prevFilter, boardBasis: newClasses };
              });
            }}
          />
        </div>

        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="shrink-0 text-xl font-bold text-black">
              {intl.formatMessage({
                id: "hotels.filters.hotelClass",
                defaultMessage: "Hotel Class",
              })}
            </h3>
            <Button
              onClick={(e) => {
                if (areHotelClassesInDefaultState) {
                  setLocalFilters((prevFilters) => ({
                    ...prevFilters,
                    starRating: HOTEL_CLASSES,
                  }));
                  setManuallySelectedAllHotelClasses(true);
                } else {
                  setLocalFilters((prevFilters) => ({
                    ...prevFilters,
                    starRating: defaultFilters.starRating,
                  }));
                  setManuallySelectedAllHotelClasses(false);
                }

                // Remove focus from the button after click
                e.currentTarget.blur();
              }}
              variant="text"
              size="small"
            >
              {areHotelClassesInDefaultState
                ? intl.formatMessage({
                    id: "selectAll",
                    defaultMessage: "Select All",
                  })
                : intl.formatMessage({
                    id: "clearFilter",
                    defaultMessage: "Clear filter",
                  })}
            </Button>
          </div>

          <HotelClassFilter
            selectedClasses={localFilters.starRating}
            userManuallySelectedAllClasses={manuallySelectedAllHotelClasses}
            onChange={(_e, checked, option) => {
              setLocalFilters((prevFilter) => {
                const newClasses = toggleHotelClasses(
                  prevFilter.starRating,
                  option,
                  checked,
                ).sort((a, b) => a - b);
                return { ...prevFilter, starRating: newClasses };
              });
            }}
          />
        </div>
      </div>

      <div className="-mx-main border-t border-t-stroke-neutral-soft px-main pt-4">
        <Button
          fullWidth
          onClick={() => onFiltersChange(localFilters, sort)}
          variant="contained"
          size="large"
        >
          {intl.formatMessage({ id: "apply", defaultMessage: "Apply" })}
        </Button>
      </div>
    </>
  );
}
